import React, { useState, useRef, useEffect, useCallback } from 'react'
import { IoArrowBack, IoChevronBack, IoChevronForward } from 'react-icons/io5'
import type { TeamLite, FixtureLite, SquadPlayer, SquadResponse, Coach } from '../types'
import { StatBar } from './StatBar'
import { apiGet, getTeamFixturesPaginated, type PaginatedFixturesResponse } from '../api'
import { HistoricalFixtureModal } from './match-analysis/components/HistoricalFixtureModal'
import { PlayerProfileModal } from './PlayerProfileModal'
import { getLeagueLogo } from '../utils/leagueBadge'

type TeamTab = 'overview' | 'table' | 'fixtures' | 'squad' | 'stats'

// Types for API response
interface StandingRow {
  position: number
  team_id: number
  team_name: string
  team_crest?: string | null
  played: number
  wins: number
  draws: number
  losses: number
  goals_for: number
  goals_against: number
  goal_difference: number
  points: number
  form?: string | null
}

interface FormFixture {
  fixture_id: number
  date: string
  opponent: TeamLite
  is_home: boolean
  score_for: number
  score_against: number
  result: 'W' | 'D' | 'L'
  league_name?: string | null
}

interface SeasonStats {
  played_home: number
  played_away: number
  wins_home: number
  wins_away: number
  draws_home: number
  draws_away: number
  losses_home: number
  losses_away: number
  goals_for_home: number
  goals_for_away: number
  goals_against_home: number
  goals_against_away: number
  clean_sheets_home: number
  clean_sheets_away: number
  form?: string | null
}

interface TeamPageData {
  team: TeamLite
  country_name?: string | null
  league_name?: string | null
  league_logo?: string | null
  standings?: StandingRow[] | null
  form?: FormFixture[] | null
  upcoming_fixtures?: FixtureLite[] | null
  past_fixtures?: FixtureLite[] | null
  season_stats?: SeasonStats | null
  squad?: SquadPlayer[] | null
  league_next_fixtures?: FixtureLite[] | null  // Next round for all league teams
  all_teams_form?: Record<string, FormFixture[]> | null  // Form details for all teams in standings (keys are string due to JSON)
}

interface TeamPageProps {
  team: TeamLite
  initialData?: TeamPageData | null
  onBack: () => void
  onFixtureClick?: (fixture: FixtureLite) => void
  onTeamClick?: (team: TeamLite) => void
}

// Get short team name for FDR badges
function getShortName(name: string) {
  const abbrevs: Record<string, string> = {
    'Manchester United': 'MUN', 'Manchester City': 'MCI', 'Tottenham Hotspur': 'TOT',
    'West Ham United': 'WHU', 'Newcastle United': 'NEW', 'Brighton & Hove Albion': 'BHA',
    'Nottingham Forest': 'NFO', 'Crystal Palace': 'CRY', 'Aston Villa': 'AVL',
    'Wolverhampton Wanderers': 'WOL', 'Borussia Dortmund': 'BVB', 'Bayern München': 'FCB',
    'RB Leipzig': 'RBL', 'Bayer Leverkusen': 'B04', 'Borussia Mönchengladbach': 'BMG',
  }
  if (abbrevs[name]) return abbrevs[name]
  const words = name.split(' ')
  if (words.length === 1) return name.slice(0, 3).toUpperCase()
  return words[0].slice(0, 3).toUpperCase()
}

// Get FDR color based on opponent position
function getFDRColor(position: number | undefined, totalTeams: number): string {
  if (!position) return 'var(--fdr-medium)'
  const percentile = position / totalTeams
  if (percentile <= 0.2) return 'var(--fdr-very-hard)'
  if (percentile <= 0.4) return 'var(--fdr-hard)'
  if (percentile <= 0.6) return 'var(--fdr-medium)'
  if (percentile <= 0.8) return 'var(--fdr-easy)'
  return 'var(--fdr-very-easy)'
}

// Format kickoff time
function formatKickoffTime(dateStr: string) {
  const date = new Date(dateStr)
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

// Format kickoff date
function formatKickoffDate(dateStr: string) {
  const date = new Date(dateStr)
  const now = new Date()
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  if (date.toDateString() === now.toDateString()) return 'Today'
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow'
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
}

// Render form badges (W/D/L colored boxes) - FotMob style
// Most recent fixture on the right with underline indicator
// When formDetails is provided (for current team), use it as the source of truth
// When onFixtureClick is provided, badges are clickable to open match stats modal
function FormBadges({
  form,
  formDetails,
  onFixtureClick
}: {
  form: string | null | undefined
  formDetails?: FormFixture[] | null
  onFixtureClick?: (fixtureId: number) => void
}) {
  // If we have formDetails, use it exclusively (accurate W/D/L + opponent info)
  if (formDetails && formDetails.length > 0) {
    // formDetails is ordered most recent first, reverse so most recent is on right
    const reversed = [...formDetails].slice(0, 5).reverse()

    return (
      <div className="form-badges">
        {reversed.map((detail, idx) => {
          const venue = detail.is_home ? 'H' : 'A'
          const tooltip = `${detail.opponent.name} (${venue})\n${detail.score_for}-${detail.score_against}`
          const isMostRecent = idx === reversed.length - 1
          const isClickable = Boolean(onFixtureClick && detail.fixture_id)

          const handleClick = (e: React.MouseEvent) => {
            e.stopPropagation()
            if (onFixtureClick && detail.fixture_id) {
              onFixtureClick(detail.fixture_id)
            }
          }

          return (
            <span
              key={idx}
              className={`form-badge-mini ${detail.result.toLowerCase()} ${isMostRecent ? 'most-recent' : ''} ${isClickable ? 'clickable' : ''}`}
              title={tooltip}
              onClick={isClickable ? handleClick : undefined}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onKeyDown={isClickable ? (e) => e.key === 'Enter' && handleClick(e as unknown as React.MouseEvent) : undefined}
            >
              {detail.result}
            </span>
          )
        })}
      </div>
    )
  }

  // Fallback to form string (for other teams in standings - no detailed data available)
  if (!form) return null

  // API-Football form string: most recent is on the LEFT, so reverse for display (most recent on right)
  const formChars = form.split('').slice(-5).reverse()

  return (
    <div className="form-badges">
      {formChars.map((char, idx) => {
        const resultLabels: Record<string, string> = { W: 'Win', D: 'Draw', L: 'Loss' }
        const tooltip = resultLabels[char.toUpperCase()] || char
        const isMostRecent = idx === formChars.length - 1

        return (
          <span
            key={idx}
            className={`form-badge-mini ${char.toLowerCase()} ${isMostRecent ? 'most-recent' : ''}`}
            title={tooltip}
          >
            {char}
          </span>
        )
      })}
    </div>
  )
}

/**
 * TeamPage - FotMob-style team overview with lazy loading
 */
export function TeamPage({
  team,
  initialData,
  onBack,
  onFixtureClick,
  onTeamClick,
}: TeamPageProps) {
  const [activeTab, setActiveTab] = useState<TeamTab>('overview')
  const [data, setData] = useState<TeamPageData | null>(initialData || null)
  const [loading, setLoading] = useState(!initialData)
  const [error, setError] = useState<string | null>(null)
  const carouselRef = useRef<HTMLDivElement>(null)

  // Squad state with lazy loading
  const [squadData, setSquadData] = useState<SquadResponse | null>(null)
  const [squadLoading, setSquadLoading] = useState(false)
  const [squadError, setSquadError] = useState<string | null>(null)
  const [squadSortKey, setSquadSortKey] = useState<keyof SquadPlayer>('games')
  const [squadSortAsc, setSquadSortAsc] = useState(false)

  // Table (standings) state with lazy loading
  const [standingsData, setStandingsData] = useState<StandingRow[] | null>(null)
  const [standingsLoading, setStandingsLoading] = useState(false)
  const [standingsLoaded, setStandingsLoaded] = useState(false)

  // Stats state with lazy loading
  const [statsData, setStatsData] = useState<SeasonStats | null>(null)
  const [statsLoading, setStatsLoading] = useState(false)
  const [statsLoaded, setStatsLoaded] = useState(false)

  // Fixtures pagination - show 5 fixtures at a time (overview tab only)
  const FIXTURES_PER_PAGE = 5
  const [overviewFixturePage, setOverviewFixturePage] = useState(0)

  // Fixtures tab - infinite scroll state
  const [fixturesTabPast, setFixturesTabPast] = useState<FixtureLite[]>([])
  const [fixturesTabUpcoming, setFixturesTabUpcoming] = useState<FixtureLite[]>([])
  const [fixturesTabHasMorePast, setFixturesTabHasMorePast] = useState(true)
  const [fixturesTabHasMoreFuture, setFixturesTabHasMoreFuture] = useState(true)
  const [fixturesTabLoadingMore, setFixturesTabLoadingMore] = useState<'past' | 'future' | null>(null)
  const [fixturesTabInitialized, setFixturesTabInitialized] = useState(false)
  const fixturesContainerRef = useRef<HTMLDivElement>(null)
  const fixturesDividerRef = useRef<HTMLDivElement>(null)

  // State for historical fixture modal (match stats)
  const [historicalFixtureId, setHistoricalFixtureId] = useState<number | null>(null)

  // State for squad player stats modal
  const [selectedSquadPlayer, setSelectedSquadPlayer] = useState<SquadPlayer | null>(null)

  const handleFormFixtureClick = useCallback((fixtureId: number) => {
    setHistoricalFixtureId(fixtureId)
  }, [])

  const handleCloseModal = useCallback(() => {
    setHistoricalFixtureId(null)
  }, [])

  const handleSquadPlayerClick = useCallback((player: SquadPlayer) => {
    setSelectedSquadPlayer(player)
  }, [])

  // Fetch data on mount
  useEffect(() => {
    if (initialData) {
      setData(initialData)
      setLoading(false)
      return
    }

    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)
        console.log(`[TeamPage] Fetching data for team ${team.id}`)
        const result = await apiGet<TeamPageData>(`/teams/${team.id}`)
        console.log(`[TeamPage] Data received:`, result)
        setData(result)
      } catch (err) {
        console.error(`[TeamPage] Fetch error:`, err)
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [team.id, initialData])

  // Lazy load squad data when tab is selected
  useEffect(() => {
    if (activeTab !== 'squad' || squadData || squadLoading) return

    const fetchSquad = async () => {
      try {
        setSquadLoading(true)
        setSquadError(null)
        console.log(`[TeamPage] Fetching squad for team ${team.id}`)
        const result = await apiGet<SquadResponse>(`/teams/${team.id}/squad`)
        console.log(`[TeamPage] Squad received:`, result)
        setSquadData(result)
      } catch (err) {
        console.error(`[TeamPage] Squad fetch error:`, err)
        setSquadError(err instanceof Error ? err.message : 'Failed to load squad')
      } finally {
        setSquadLoading(false)
      }
    }

    fetchSquad()
  }, [activeTab, team.id, squadData, squadLoading])

  // Lazy load standings (table) when table tab is selected or overview needs it
  useEffect(() => {
    // Load standings for table tab OR overview tab (for mini table display)
    const needsStandings = activeTab === 'table' || activeTab === 'overview'
    if (!needsStandings || standingsLoaded || standingsLoading) return

    const fetchStandings = async () => {
      try {
        setStandingsLoading(true)
        console.log(`[TeamPage] Fetching standings for team ${team.id}`)
        const result = await apiGet<StandingRow[]>(`/teams/${team.id}/standings`)
        console.log(`[TeamPage] Standings received:`, result)
        setStandingsData(result)
        setStandingsLoaded(true)
      } catch (err) {
        console.error(`[TeamPage] Standings fetch error:`, err)
        setStandingsLoaded(true) // Mark as loaded to prevent retries
      } finally {
        setStandingsLoading(false)
      }
    }

    fetchStandings()
  }, [activeTab, team.id, standingsLoaded, standingsLoading])

  // Lazy load stats when stats tab is selected or overview needs it
  useEffect(() => {
    // Load stats for stats tab OR overview tab (for season stats card)
    const needsStats = activeTab === 'stats' || activeTab === 'overview'
    if (!needsStats || statsLoaded || statsLoading) return

    const fetchStats = async () => {
      try {
        setStatsLoading(true)
        console.log(`[TeamPage] Fetching stats for team ${team.id}`)
        const result = await apiGet<SeasonStats>(`/teams/${team.id}/stats`)
        console.log(`[TeamPage] Stats received:`, result)
        setStatsData(result)
        setStatsLoaded(true)
      } catch (err) {
        console.error(`[TeamPage] Stats fetch error:`, err)
        setStatsLoaded(true) // Mark as loaded to prevent retries
      } finally {
        setStatsLoading(false)
      }
    }

    fetchStats()
  }, [activeTab, team.id, statsLoaded, statsLoading])

  // Lazy load fixtures tab data when tab is selected
  useEffect(() => {
    if (activeTab !== 'fixtures' || fixturesTabInitialized || fixturesTabLoadingMore) return

    const fetchFixtures = async () => {
      try {
        setFixturesTabLoadingMore('past') // Use 'past' as loading indicator for initial load
        console.log(`[TeamPage] Fetching paginated fixtures for team ${team.id}`)
        const result = await getTeamFixturesPaginated(team.id, {
          direction: 'both',
          limit: 30,
        })
        console.log(`[TeamPage] Paginated fixtures received:`, result)
        setFixturesTabPast(result.past_fixtures)
        setFixturesTabUpcoming(result.upcoming_fixtures)
        setFixturesTabHasMorePast(result.has_more_past)
        setFixturesTabHasMoreFuture(result.has_more_future)
        setFixturesTabInitialized(true)
      } catch (err) {
        console.error(`[TeamPage] Fixtures fetch error:`, err)
      } finally {
        setFixturesTabLoadingMore(null)
      }
    }

    fetchFixtures()
  }, [activeTab, team.id, fixturesTabInitialized, fixturesTabLoadingMore])

  // Scroll to divider after fixtures tab loads
  useEffect(() => {
    if (activeTab === 'fixtures' && fixturesTabInitialized && fixturesDividerRef.current) {
      // Small delay to ensure DOM is rendered
      requestAnimationFrame(() => {
        fixturesDividerRef.current?.scrollIntoView({ block: 'center', behavior: 'auto' })
      })
    }
  }, [activeTab, fixturesTabInitialized])

  // Load more fixtures function
  const loadMoreFixtures = useCallback(async (direction: 'past' | 'future') => {
    if (fixturesTabLoadingMore) return

    setFixturesTabLoadingMore(direction)

    try {
      const params = direction === 'past'
        ? { direction: 'past' as const, limit: 15, beforeDate: fixturesTabPast[0]?.kickoff_utc }
        : { direction: 'future' as const, limit: 15, afterDate: fixturesTabUpcoming[fixturesTabUpcoming.length - 1]?.kickoff_utc }

      const container = fixturesContainerRef.current
      const scrollHeight = container?.scrollHeight || 0

      const result = await getTeamFixturesPaginated(team.id, params)

      if (direction === 'past') {
        setFixturesTabPast(prev => [...result.past_fixtures, ...prev])
        setFixturesTabHasMorePast(result.has_more_past)
        // Restore scroll position when prepending
        if (container) {
          requestAnimationFrame(() => {
            container.scrollTop += container.scrollHeight - scrollHeight
          })
        }
      } else {
        setFixturesTabUpcoming(prev => [...prev, ...result.upcoming_fixtures])
        setFixturesTabHasMoreFuture(result.has_more_future)
      }
    } catch (err) {
      console.error(`[TeamPage] Load more fixtures error:`, err)
    } finally {
      setFixturesTabLoadingMore(null)
    }
  }, [team.id, fixturesTabPast, fixturesTabUpcoming, fixturesTabLoadingMore])

  // Fixtures scroll handler for infinite scroll
  const handleFixturesScroll = useCallback(() => {
    const container = fixturesContainerRef.current
    if (!container || fixturesTabLoadingMore) return

    // Load more past when near top (< 200px from top)
    if (container.scrollTop < 200 && fixturesTabHasMorePast) {
      loadMoreFixtures('past')
    }

    // Load more future when near bottom (< 200px from bottom)
    const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200
    if (nearBottom && fixturesTabHasMoreFuture) {
      loadMoreFixtures('future')
    }
  }, [fixturesTabLoadingMore, fixturesTabHasMorePast, fixturesTabHasMoreFuture, loadMoreFixtures])

  // Check if there's a match today in upcoming fixtures
  const hasTodayMatch = fixturesTabUpcoming.some(f => {
    const matchDate = new Date(f.kickoff_utc)
    const today = new Date()
    return matchDate.toDateString() === today.toDateString()
  })

  // Handle squad column sort
  const handleSquadSort = (key: keyof SquadPlayer) => {
    if (squadSortKey === key) {
      setSquadSortAsc(!squadSortAsc)
    } else {
      setSquadSortKey(key)
      setSquadSortAsc(false) // Default to descending for stats
    }
  }

  // Get sorted squad players
  const sortedSquadPlayers = squadData?.players?.slice().sort((a, b) => {
    const aVal = a[squadSortKey]
    const bVal = b[squadSortKey]

    // Handle nulls
    if (aVal == null && bVal == null) return 0
    if (aVal == null) return squadSortAsc ? -1 : 1
    if (bVal == null) return squadSortAsc ? 1 : -1

    // String comparison for name/position
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return squadSortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
    }

    // Numeric comparison
    const numA = Number(aVal) || 0
    const numB = Number(bVal) || 0
    return squadSortAsc ? numA - numB : numB - numA
  }) || []


  // Carousel scroll
  const scrollCarousel = (direction: 'left' | 'right') => {
    if (carouselRef.current) {
      const scrollAmount = 200
      carouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  // Get opponent position for FDR - use lazy-loaded standings
  const getOpponentPosition = (opponentId: number) => {
    return standingsData?.find(s => s.team_id === opponentId)?.position
  }

  const TABS: { id: TeamTab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'table', label: 'Table' },
    { id: 'fixtures', label: 'Fixtures' },
    { id: 'squad', label: 'Squad' },
    { id: 'stats', label: 'Stats' },
  ]

  // Calculate totals - use lazy-loaded stats
  const stats = statsData
  const totalPlayed = stats ? stats.played_home + stats.played_away : 0
  const totalWins = stats ? stats.wins_home + stats.wins_away : 0
  const totalDraws = stats ? stats.draws_home + stats.draws_away : 0
  const totalLosses = stats ? stats.losses_home + stats.losses_away : 0

  // Get next match
  const nextMatch = data?.upcoming_fixtures?.[0]

  return (
    <div className="team-page">
      {/* Header */}
      <div className="team-page-header">
        <div className="team-page-header-top">
          <button className="team-page-back" onClick={onBack} aria-label="Go back">
            <IoArrowBack size={20} />
          </button>
          <div className="team-page-header-main">
            {team.crest_url && (
              <img src={team.crest_url} alt={team.name} className="team-page-logo" />
            )}
            <div className="team-page-name-section">
              <h1 className="team-page-name">{team.name}</h1>
              {data?.country_name && (
                <span className="team-page-country">{data.country_name}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="team-page-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`team-page-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="team-page-loading">
          <div className="spinner" />
          <span>Loading team data...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="team-page-error">
          <span>Failed to load team data</span>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      )}

      {/* Tab Content */}
      {!loading && !error && (
        <div className="team-page-content">
          {activeTab === 'overview' && (
            <div className="team-overview-grid">
              {/* ROW 1, COL 1: Team Form Card */}
              <div className="fm-card team-form-card">
                <div className="fm-card-header">Team form</div>
                <div className="fm-card-body">
                  {data?.form && data.form.length > 0 ? (
                    <div className="team-form-grid">
                      {/* Reverse so most recent is on the right */}
                      {[...data.form.slice(0, 5)].reverse().map((f, idx, arr) => {
                        const isMostRecent = idx === arr.length - 1
                        return (
                          <button
                            key={idx}
                            type="button"
                            className={`team-form-item ${isMostRecent ? 'most-recent' : ''}`}
                            onClick={() => handleFormFixtureClick(f.fixture_id)}
                          >
                            <div className={`team-form-score-badge ${f.result.toLowerCase()}`}>
                              {f.score_for} - {f.score_against}
                            </div>
                            {f.opponent.crest_url ? (
                              <img src={f.opponent.crest_url} alt="" className="team-form-opponent-logo" />
                            ) : (
                              <div className="team-form-opponent-placeholder">{f.opponent.name.charAt(0)}</div>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="team-form-empty">No recent results</div>
                  )}
                </div>
              </div>

              {/* ROW 1, COL 2: Next/Ongoing Match Card */}
              {(() => {
                const isLive = nextMatch && ['1H', '2H', 'HT', 'ET', 'P', 'LIVE'].includes(nextMatch.status || '')
                return (
                  <div className="fm-card team-next-match-card" onClick={() => nextMatch && onFixtureClick?.(nextMatch)}>
                    <div className="fm-card-header">
                      <span>{isLive ? 'Ongoing' : 'Next fixture'}</span>
                      {nextMatch?.league_name && (
                        <span className="team-next-match-competition">{nextMatch.league_name}</span>
                      )}
                    </div>
                    <div className="fm-card-body">
                      {nextMatch ? (
                        <div className="team-next-match-layout">
                          <div className="team-next-match-team">
                            {nextMatch.home_team.crest_url ? (
                              <img src={nextMatch.home_team.crest_url} alt="" className="team-next-match-logo" />
                            ) : (
                              <div className="team-next-match-logo-placeholder">{nextMatch.home_team.name.charAt(0)}</div>
                            )}
                            <span className="team-next-match-name">{nextMatch.home_team.name}</span>
                          </div>
                          <div className="team-next-match-center">
                            {isLive ? (
                              <>
                                <span className="team-next-match-live-score">
                                  {nextMatch.home_score ?? 0} - {nextMatch.away_score ?? 0}
                                </span>
                                <span className="team-next-match-live-status">{nextMatch.status}</span>
                              </>
                            ) : (
                              <>
                                <span className="team-next-match-time">{formatKickoffTime(nextMatch.kickoff_utc)}</span>
                                <span className="team-next-match-date">{formatKickoffDate(nextMatch.kickoff_utc)}</span>
                              </>
                            )}
                          </div>
                          <div className="team-next-match-team">
                            {nextMatch.away_team.crest_url ? (
                              <img src={nextMatch.away_team.crest_url} alt="" className="team-next-match-logo" />
                            ) : (
                              <div className="team-next-match-logo-placeholder">{nextMatch.away_team.name.charAt(0)}</div>
                            )}
                            <span className="team-next-match-name">{nextMatch.away_team.name}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="team-next-match-empty">No upcoming matches</div>
                      )}
                    </div>
                  </div>
                )
              })()}

              {/* ROW 1, COL 3: Season Stats Card */}
              <div className="fm-card team-season-stats-card">
                <div className="fm-card-header">
                  {data?.league_logo && <img src={data.league_logo} alt="" className="team-table-league-logo" />}
                  <span>Season stats</span>
                </div>
                <div className="fm-card-body">
                  {statsLoading ? (
                    <div className="season-stats-grid skeleton">
                      <div className="season-stat-item"><div className="skeleton-text" style={{ width: 24, height: 20 }} /></div>
                      <div className="season-stat-item"><div className="skeleton-text" style={{ width: 24, height: 20 }} /></div>
                      <div className="season-stat-item"><div className="skeleton-text" style={{ width: 24, height: 20 }} /></div>
                      <div className="season-stat-item"><div className="skeleton-text" style={{ width: 24, height: 20 }} /></div>
                    </div>
                  ) : stats ? (
                    <div className="season-stats-grid">
                      <div className="season-stat-item">
                        <span className="season-stat-value">{totalPlayed}</span>
                        <span className="season-stat-label">Played</span>
                      </div>
                      <div className="season-stat-item win">
                        <span className="season-stat-value">{totalWins}</span>
                        <span className="season-stat-label">Won</span>
                      </div>
                      <div className="season-stat-item draw">
                        <span className="season-stat-value">{totalDraws}</span>
                        <span className="season-stat-label">Drawn</span>
                      </div>
                      <div className="season-stat-item loss">
                        <span className="season-stat-value">{totalLosses}</span>
                        <span className="season-stat-label">Lost</span>
                      </div>
                    </div>
                  ) : (
                    <div className="team-form-empty">No stats available</div>
                  )}
                </div>
              </div>

              {/* ROW 2, COL 1-2: League Table (spans 2 columns) */}
              <div className="fm-card team-standings-card">
                <div className="fm-card-header">
                  <div className="team-table-header-left">
                    {(data?.league_logo || getLeagueLogo(data?.league_name)) && (
                      <img src={data?.league_logo || getLeagueLogo(data?.league_name) || ''} alt="" className="team-table-league-logo" />
                    )}
                    <span>{data?.league_name || 'League Table'}</span>
                  </div>
                </div>
                <div className="fm-card-body team-table-body">
                  {standingsLoading ? (
                    <div className="standings-skeleton">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="skeleton-row" style={{ height: 36, marginBottom: 4 }}>
                          <div className="skeleton-text" style={{ width: '100%', height: '100%' }} />
                        </div>
                      ))}
                    </div>
                  ) : standingsData && standingsData.length > 0 ? (
                    <table className="team-standings-table">
                      <thead>
                        <tr>
                          <th className="col-pos">#</th>
                          <th className="col-team"></th>
                          <th className="col-pl">PL</th>
                          <th className="col-w">W</th>
                          <th className="col-d">D</th>
                          <th className="col-l">L</th>
                          <th className="col-gf">+/-</th>
                          <th className="col-gd">GD</th>
                          <th className="col-pts">PTS</th>
                          <th className="col-form">Form</th>
                          <th className="col-next">Next</th>
                        </tr>
                      </thead>
                      <tbody>
                        {standingsData.map(row => {
                          // Use league_next_fixtures to find each team's next opponent
                          const nextOpp = data?.league_next_fixtures?.find(f =>
                            f.home_team.id === row.team_id || f.away_team.id === row.team_id
                          )
                          const nextOppTeam = nextOpp
                            ? (nextOpp.home_team.id === row.team_id ? nextOpp.away_team : nextOpp.home_team)
                            : null

                          return (
                            <tr
                              key={row.team_id}
                              className={row.team_id === team.id ? 'highlighted' : ''}
                              onClick={() => row.team_id !== team.id && onTeamClick?.({
                                id: row.team_id,
                                name: row.team_name,
                                crest_url: row.team_crest
                              })}
                            >
                              <td className="col-pos">{row.position}</td>
                              <td className="col-team">
                                <div className="table-team-cell">
                                  {row.team_crest && <img src={row.team_crest} alt="" className="table-team-logo" />}
                                  <span className="table-team-name">{row.team_name}</span>
                                </div>
                              </td>
                              <td className="col-pl">{row.played}</td>
                              <td className="col-w">{row.wins}</td>
                              <td className="col-d">{row.draws}</td>
                              <td className="col-l">{row.losses}</td>
                              <td className="col-gf">{row.goals_for}-{row.goals_against}</td>
                              <td className="col-gd">{row.goal_difference > 0 ? `+${row.goal_difference}` : row.goal_difference}</td>
                              <td className="col-pts">{row.points}</td>
                              <td className="col-form">
                                <FormBadges
                                  form={row.form}
                                  formDetails={data?.all_teams_form?.[String(row.team_id)]}
                                  onFixtureClick={handleFormFixtureClick}
                                />
                              </td>
                              <td className="col-next">
                                {nextOpp && nextOppTeam && (() => {
                                  const isHome = nextOpp.home_team.id === row.team_id
                                  const kickoff = new Date(nextOpp.kickoff_utc)
                                  const dateStr = kickoff.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
                                  const timeStr = kickoff.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                                  const tooltip = `${nextOppTeam.name} (${isHome ? 'H' : 'A'})\n${dateStr} ${timeStr}`

                                  return nextOppTeam.crest_url ? (
                                    <img
                                      src={nextOppTeam.crest_url}
                                      alt={nextOppTeam.name}
                                      title={tooltip}
                                      className="next-opp-logo"
                                    />
                                  ) : (
                                    <div className="next-opp-placeholder" title={tooltip}>
                                      {nextOppTeam.name.charAt(0)}
                                    </div>
                                  )
                                })()}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className="team-empty-state">No standings available</div>
                  )}
                </div>
              </div>

              {/* ROW 2-3, COL 3: FDR + Fixtures Carousel */}
              <div className="team-overview-col-right">
                {/* Fixture Difficulty */}
                {data?.upcoming_fixtures && data.upcoming_fixtures.length > 0 && (
                  <div className="fm-card team-fdr-card">
                    <div className="fm-card-header">
                      <span>Fixture difficulty</span>
                      <div className="team-fdr-header-right">
                        <span className="team-fdr-subtitle">Next five matches</span>
                        {data.league_logo && <img src={data.league_logo} alt="" className="team-fdr-league-logo" />}
                      </div>
                    </div>
                    <div className="fm-card-body">
                      <div className="team-fdr-badges">
                        {data.upcoming_fixtures.slice(0, 5).map((fixture, idx) => {
                          const isHome = fixture.home_team.id === team.id
                          const opponent = isHome ? fixture.away_team : fixture.home_team
                          const venueLabel = isHome ? 'H' : 'A'
                          const oppPosition = getOpponentPosition(opponent.id)
                          const fdrColor = getFDRColor(oppPosition, data.standings?.length || 20)

                          return (
                            <button
                              key={idx}
                              type="button"
                              className="team-fdr-badge"
                              onClick={() => onFixtureClick?.(fixture)}
                              style={{ backgroundColor: fdrColor }}
                            >
                              <span className="team-fdr-opponent">{getShortName(opponent.name)}</span>
                              <span className="team-fdr-venue">({venueLabel})</span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* FotMob-style Upcoming Fixtures */}
                {(() => {
                  const upcomingFixtures = data?.upcoming_fixtures || []
                  const totalPages = Math.ceil(upcomingFixtures.length / FIXTURES_PER_PAGE)
                  const startIdx = overviewFixturePage * FIXTURES_PER_PAGE
                  const visibleFixtures = upcomingFixtures.slice(startIdx, startIdx + FIXTURES_PER_PAGE)
                  const canGoPrev = overviewFixturePage > 0
                  const canGoNext = overviewFixturePage < totalPages - 1

                  return (
                    <div className="fm-card team-upcoming-fixtures-card">
                      <div className="fotmob-fixtures-header">
                        <button
                          className="fotmob-nav-btn"
                          disabled={!canGoPrev}
                          onClick={() => setOverviewFixturePage(p => Math.max(0, p - 1))}
                        >
                          <IoChevronBack size={20} />
                        </button>
                        <h3 className="fotmob-fixtures-title">Fixtures</h3>
                        <button
                          className="fotmob-nav-btn"
                          disabled={!canGoNext}
                          onClick={() => setOverviewFixturePage(p => Math.min(totalPages - 1, p + 1))}
                        >
                          <IoChevronForward size={20} />
                        </button>
                      </div>
                      <div className="fotmob-fixtures-list">
                        {visibleFixtures.length > 0 ? (
                          visibleFixtures.map(fixture => (
                            <TeamFixtureRow
                              key={fixture.id}
                              fixture={fixture}
                              teamId={team.id}
                              onClick={() => onFixtureClick?.(fixture)}
                              leagueLogo={data?.league_logo}
                            />
                          ))
                        ) : (
                          <div className="team-empty-state">No upcoming fixtures</div>
                        )}
                      </div>
                    </div>
                  )
                })()}
              </div>
            </div>
          )}

          {activeTab === 'table' && (
            <div className="team-table-tab">
              {standingsLoading ? (
                <div className="fm-card">
                  <div className="fm-card-header">
                    <div className="team-table-header-left">
                      <span>Loading standings...</span>
                    </div>
                  </div>
                  <div className="fm-card-body team-table-body full-table">
                    <div className="standings-skeleton">
                      {[...Array(10)].map((_, i) => (
                        <div key={i} className="skeleton-row" style={{ height: 40, marginBottom: 4 }}>
                          <div className="skeleton-text" style={{ width: '100%', height: '100%' }} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : standingsData && standingsData.length > 0 ? (
                <div className="fm-card">
                  <div className="fm-card-header">
                    <div className="team-table-header-left">
                      {(data?.league_logo || getLeagueLogo(data?.league_name)) && (
                        <img src={data?.league_logo || getLeagueLogo(data?.league_name) || ''} alt="" className="team-table-league-logo" />
                      )}
                      <span>{data?.league_name || 'League Table'}</span>
                    </div>
                  </div>
                  <div className="fm-card-body team-table-body full-table">
                    <table className="team-standings-table">
                      <thead>
                        <tr>
                          <th className="col-pos">#</th>
                          <th className="col-team">Team</th>
                          <th className="col-pl">PL</th>
                          <th className="col-w">W</th>
                          <th className="col-d">D</th>
                          <th className="col-l">L</th>
                          <th className="col-gf">+/-</th>
                          <th className="col-gd">GD</th>
                          <th className="col-pts">PTS</th>
                          <th className="col-form">Form</th>
                        </tr>
                      </thead>
                      <tbody>
                        {standingsData.map(row => (
                          <tr
                            key={row.team_id}
                            className={row.team_id === team.id ? 'highlighted' : ''}
                            onClick={() => row.team_id !== team.id && onTeamClick?.({
                              id: row.team_id,
                              name: row.team_name,
                              crest_url: row.team_crest
                            })}
                          >
                            <td className="col-pos">{row.position}</td>
                            <td className="col-team">
                              <div className="table-team-cell">
                                {row.team_crest && <img src={row.team_crest} alt="" className="table-team-logo" />}
                                <span className="table-team-name">{row.team_name}</span>
                              </div>
                            </td>
                            <td className="col-pl">{row.played}</td>
                            <td className="col-w">{row.wins}</td>
                            <td className="col-d">{row.draws}</td>
                            <td className="col-l">{row.losses}</td>
                            <td className="col-gf">{row.goals_for}-{row.goals_against}</td>
                            <td className="col-gd">{row.goal_difference > 0 ? `+${row.goal_difference}` : row.goal_difference}</td>
                            <td className="col-pts">{row.points}</td>
                            <td className="col-form">
                              <FormBadges
                                form={row.form}
                                formDetails={data?.all_teams_form?.[String(row.team_id)]}
                                onFixtureClick={handleFormFixtureClick}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="team-empty-state">No standings available</div>
              )}
            </div>
          )}

          {activeTab === 'fixtures' && (
            <div className="team-fixtures-tab fotmob-fixtures">
              {/* Header */}
              <div className="fotmob-fixtures-header">
                <h3 className="fotmob-fixtures-title">Fixtures</h3>
              </div>

              {/* Scrollable fixtures container */}
              <div
                className="fixtures-scroll-container"
                ref={fixturesContainerRef}
                onScroll={handleFixturesScroll}
              >
                {/* Loading indicator for past fixtures */}
                {fixturesTabLoadingMore === 'past' && (
                  <div className="fixtures-loading-more">Loading earlier fixtures...</div>
                )}

                {/* Past fixtures (oldest to most recent) - open modal for completed matches */}
                {fixturesTabPast.map(fixture => (
                  <TeamFixtureRow
                    key={fixture.id}
                    fixture={fixture}
                    teamId={team.id}
                    onClick={() => handleFormFixtureClick(fixture.id)}
                    leagueLogo={data?.league_logo}
                  />
                ))}

                {/* Divider between past and upcoming */}
                {(fixturesTabPast.length > 0 || fixturesTabUpcoming.length > 0) && (
                  <div className="fixtures-divider" ref={fixturesDividerRef}>
                    <span className="fixtures-divider-text">
                      {hasTodayMatch ? 'TODAY' : 'UPCOMING'}
                    </span>
                  </div>
                )}

                {/* Upcoming fixtures (soonest first) - navigate to pre-match page */}
                {fixturesTabUpcoming.map(fixture => (
                  <TeamFixtureRow
                    key={fixture.id}
                    fixture={fixture}
                    teamId={team.id}
                    onClick={() => onFixtureClick?.(fixture)}
                    leagueLogo={data?.league_logo}
                  />
                ))}

                {/* Loading indicator for future fixtures */}
                {fixturesTabLoadingMore === 'future' && (
                  <div className="fixtures-loading-more">Loading more fixtures...</div>
                )}

                {/* Empty state */}
                {fixturesTabInitialized && fixturesTabPast.length === 0 && fixturesTabUpcoming.length === 0 && (
                  <div className="team-empty-state">No fixtures available</div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'squad' && (
            <div className="team-squad-tab">
              {squadLoading && (
                <div className="team-loading">Loading squad...</div>
              )}
              {squadError && (
                <div className="team-error">{squadError}</div>
              )}
              {!squadLoading && !squadError && squadData && (
                <>
                  {/* Manager/Coach Section */}
                  {squadData.coach && (
                    <div className="team-coach-section">
                      <div className="team-coach-card">
                        {squadData.coach.photo_url && (
                          <img
                            src={squadData.coach.photo_url}
                            alt=""
                            className="team-coach-photo"
                          />
                        )}
                        <div className="team-coach-info">
                          <span className="team-coach-role">Manager</span>
                          <span className="team-coach-name">{squadData.coach.name}</span>
                          {squadData.coach.nationality && (
                            <span className="team-coach-nationality">{squadData.coach.nationality}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Squad Table */}
                  {sortedSquadPlayers.length > 0 ? (
                    <div className="team-squad-table-wrapper">
                      <table className="team-squad-table">
                        <thead>
                          <tr>
                            <th className="col-squad-player" onClick={() => handleSquadSort('name')}>
                              Player {squadSortKey === 'name' && (squadSortAsc ? '↑' : '↓')}
                            </th>
                            <th className="col-squad-pos" onClick={() => handleSquadSort('position')}>
                              Pos {squadSortKey === 'position' && (squadSortAsc ? '↑' : '↓')}
                            </th>
                            <th className="col-squad-num" onClick={() => handleSquadSort('games')}>
                              GP {squadSortKey === 'games' && (squadSortAsc ? '↑' : '↓')}
                            </th>
                            <th className="col-squad-num" onClick={() => handleSquadSort('minutes')}>
                              Min {squadSortKey === 'minutes' && (squadSortAsc ? '↑' : '↓')}
                            </th>
                            <th className="col-squad-num" onClick={() => handleSquadSort('goals')}>
                              G {squadSortKey === 'goals' && (squadSortAsc ? '↑' : '↓')}
                            </th>
                            <th className="col-squad-num" onClick={() => handleSquadSort('assists')}>
                              A {squadSortKey === 'assists' && (squadSortAsc ? '↑' : '↓')}
                            </th>
                            <th className="col-squad-num hide-mobile" onClick={() => handleSquadSort('rating')}>
                              Rtg {squadSortKey === 'rating' && (squadSortAsc ? '↑' : '↓')}
                            </th>
                            <th className="col-squad-num hide-mobile" onClick={() => handleSquadSort('shots')}>
                              Sh {squadSortKey === 'shots' && (squadSortAsc ? '↑' : '↓')}
                            </th>
                            <th className="col-squad-num hide-mobile" onClick={() => handleSquadSort('shots_on_target')}>
                              SoT {squadSortKey === 'shots_on_target' && (squadSortAsc ? '↑' : '↓')}
                            </th>
                            <th className="col-squad-num hide-mobile" onClick={() => handleSquadSort('key_passes')}>
                              KP {squadSortKey === 'key_passes' && (squadSortAsc ? '↑' : '↓')}
                            </th>
                            <th className="col-squad-num hide-mobile" onClick={() => handleSquadSort('pass_accuracy')}>
                              Pass% {squadSortKey === 'pass_accuracy' && (squadSortAsc ? '↑' : '↓')}
                            </th>
                            <th className="col-squad-num hide-mobile" onClick={() => handleSquadSort('tackles')}>
                              Tkl {squadSortKey === 'tackles' && (squadSortAsc ? '↑' : '↓')}
                            </th>
                            <th className="col-squad-num hide-mobile" onClick={() => handleSquadSort('interceptions')}>
                              Int {squadSortKey === 'interceptions' && (squadSortAsc ? '↑' : '↓')}
                            </th>
                            <th className="col-squad-num hide-mobile" onClick={() => handleSquadSort('duels_won')}>
                              Dls {squadSortKey === 'duels_won' && (squadSortAsc ? '↑' : '↓')}
                            </th>
                            <th className="col-squad-num hide-mobile" onClick={() => handleSquadSort('dribbles_success')}>
                              Drb {squadSortKey === 'dribbles_success' && (squadSortAsc ? '↑' : '↓')}
                            </th>
                            <th className="col-squad-num" onClick={() => handleSquadSort('yellows')}>
                              Y {squadSortKey === 'yellows' && (squadSortAsc ? '↑' : '↓')}
                            </th>
                            <th className="col-squad-num" onClick={() => handleSquadSort('reds')}>
                              R {squadSortKey === 'reds' && (squadSortAsc ? '↑' : '↓')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedSquadPlayers.map(player => (
                            <tr
                              key={player.player_id}
                              className="squad-player-row clickable"
                              onClick={() => handleSquadPlayerClick(player)}
                            >
                              <td className="col-squad-player">
                                <div className="squad-player-cell">
                                  {player.photo_url && (
                                    <img
                                      src={player.photo_url}
                                      alt=""
                                      className="squad-player-photo"
                                    />
                                  )}
                                  <div className="squad-player-info">
                                    <span className="squad-player-name">{player.name}</span>
                                    {player.number && (
                                      <span className="squad-player-number">#{player.number}</span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="col-squad-pos">{player.position || '-'}</td>
                              <td className="col-squad-num">{player.games}</td>
                              <td className="col-squad-num">{player.minutes}</td>
                              <td className="col-squad-num">{player.goals}</td>
                              <td className="col-squad-num">{player.assists}</td>
                              <td className="col-squad-num hide-mobile">{player.rating?.toFixed(1) || '-'}</td>
                              <td className="col-squad-num hide-mobile">{player.shots}</td>
                              <td className="col-squad-num hide-mobile">{player.shots_on_target}</td>
                              <td className="col-squad-num hide-mobile">{player.key_passes}</td>
                              <td className="col-squad-num hide-mobile">{player.pass_accuracy?.toFixed(0) || '-'}</td>
                              <td className="col-squad-num hide-mobile">{player.tackles}</td>
                              <td className="col-squad-num hide-mobile">{player.interceptions}</td>
                              <td className="col-squad-num hide-mobile">{player.duels_won}</td>
                              <td className="col-squad-num hide-mobile">{player.dribbles_success}</td>
                              <td className="col-squad-num">{player.yellows}</td>
                              <td className="col-squad-num">{player.reds}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="team-empty-state">No squad data available</div>
                  )}

                  {!squadData.stats_available && sortedSquadPlayers.length > 0 && (
                    <div className="team-squad-note">
                      Stats will be available once fixture data is loaded
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'stats' && (
            <div className="team-stats-tab">
              {statsLoading ? (
                <div className="stats-loading">
                  <div className="team-stats-card">
                    <div className="skeleton-text" style={{ width: 150, height: 20, marginBottom: 16 }} />
                    <div className="team-stats-grid skeleton">
                      <div className="season-stat-item"><div className="skeleton-text" style={{ width: 40, height: 32 }} /></div>
                      <div className="season-stat-item"><div className="skeleton-text" style={{ width: 40, height: 32 }} /></div>
                      <div className="season-stat-item"><div className="skeleton-text" style={{ width: 40, height: 32 }} /></div>
                      <div className="season-stat-item"><div className="skeleton-text" style={{ width: 40, height: 32 }} /></div>
                    </div>
                  </div>
                  <div className="team-stats-card">
                    <div className="skeleton-text" style={{ width: 150, height: 20, marginBottom: 16 }} />
                    <div className="team-stats-grid skeleton">
                      <div className="season-stat-item"><div className="skeleton-text" style={{ width: 40, height: 32 }} /></div>
                      <div className="season-stat-item"><div className="skeleton-text" style={{ width: 40, height: 32 }} /></div>
                      <div className="season-stat-item"><div className="skeleton-text" style={{ width: 40, height: 32 }} /></div>
                      <div className="season-stat-item"><div className="skeleton-text" style={{ width: 40, height: 32 }} /></div>
                    </div>
                  </div>
                </div>
              ) : stats ? (
                <>
                  <div className="team-stats-card">
                    <h3 className="team-stats-card-title">Home Performance</h3>
                    <div className="team-stats-grid">
                      <div className="team-stat-item">
                        <span className="team-stat-value">{stats.played_home}</span>
                        <span className="team-stat-label">Played</span>
                      </div>
                      <div className="team-stat-item win">
                        <span className="team-stat-value">{stats.wins_home}</span>
                        <span className="team-stat-label">Won</span>
                      </div>
                      <div className="team-stat-item draw">
                        <span className="team-stat-value">{stats.draws_home}</span>
                        <span className="team-stat-label">Drawn</span>
                      </div>
                      <div className="team-stat-item loss">
                        <span className="team-stat-value">{stats.losses_home}</span>
                        <span className="team-stat-label">Lost</span>
                      </div>
                    </div>
                    <div className="team-stats-bars">
                      <StatBar label="Goals For" value={stats.goals_for_home} max={Math.max(stats.goals_for_home, stats.goals_against_home, 1)} />
                      <StatBar label="Goals Against" value={stats.goals_against_home} max={Math.max(stats.goals_for_home, stats.goals_against_home, 1)} color="var(--color-loss)" />
                      <StatBar label="Clean Sheets" value={stats.clean_sheets_home} max={stats.played_home || 1} />
                    </div>
                  </div>

                  <div className="team-stats-card">
                    <h3 className="team-stats-card-title">Away Performance</h3>
                    <div className="team-stats-grid">
                      <div className="team-stat-item">
                        <span className="team-stat-value">{stats.played_away}</span>
                        <span className="team-stat-label">Played</span>
                      </div>
                      <div className="team-stat-item win">
                        <span className="team-stat-value">{stats.wins_away}</span>
                        <span className="team-stat-label">Won</span>
                      </div>
                      <div className="team-stat-item draw">
                        <span className="team-stat-value">{stats.draws_away}</span>
                        <span className="team-stat-label">Drawn</span>
                      </div>
                      <div className="team-stat-item loss">
                        <span className="team-stat-value">{stats.losses_away}</span>
                        <span className="team-stat-label">Lost</span>
                      </div>
                    </div>
                    <div className="team-stats-bars">
                      <StatBar label="Goals For" value={stats.goals_for_away} max={Math.max(stats.goals_for_away, stats.goals_against_away, 1)} />
                      <StatBar label="Goals Against" value={stats.goals_against_away} max={Math.max(stats.goals_for_away, stats.goals_against_away, 1)} color="var(--color-loss)" />
                      <StatBar label="Clean Sheets" value={stats.clean_sheets_away} max={stats.played_away || 1} />
                    </div>
                  </div>
                </>
              ) : (
                <div className="team-empty-state">No stats available</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Historical Fixture Modal - for viewing past match stats */}
      {historicalFixtureId !== null && (
        <HistoricalFixtureModal
          fixtureId={historicalFixtureId}
          onClose={handleCloseModal}
        />
      )}

      {/* Player Profile Modal */}
      {selectedSquadPlayer && (
        <PlayerProfileModal
          playerId={selectedSquadPlayer.player_id}
          onClose={() => setSelectedSquadPlayer(null)}
        />
      )}
    </div>
  )
}

// FotMob-style fixture row component
function TeamFixtureRow({
  fixture,
  teamId,
  onClick,
  leagueLogo
}: {
  fixture: FixtureLite
  teamId: number
  onClick?: () => void
  leagueLogo?: string | null
}) {
  const isHome = fixture.home_team.id === teamId
  const isFinished = fixture.status === 'FT'

  // Format date like "Sun 1 Mar"
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
  }

  // Format time like "14:00"
  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  }

  // Get league logo - use provided one or fixture-specific
  const displayLeagueLogo = leagueLogo || getLeagueLogo(fixture.league_name)

  // Determine result from team's perspective (for score color)
  const getResultClass = (): string => {
    if (!isFinished) return ''
    const teamScore = isHome ? (fixture.home_score ?? 0) : (fixture.away_score ?? 0)
    const opponentScore = isHome ? (fixture.away_score ?? 0) : (fixture.home_score ?? 0)
    if (teamScore > opponentScore) return 'win'
    if (teamScore < opponentScore) return 'loss'
    return 'draw'
  }

  const resultClass = getResultClass()

  return (
    <div className="fotmob-fixture-row" onClick={onClick}>
      {/* Date and League Header */}
      <div className="fotmob-fixture-header">
        <span className="fotmob-fixture-date">{formatDate(fixture.kickoff_utc)}</span>
        <div className="fotmob-fixture-league">
          <span className="fotmob-fixture-league-name">{fixture.league_name || 'League'}</span>
          {displayLeagueLogo && <img src={displayLeagueLogo} alt="" className="fotmob-fixture-league-logo" />}
        </div>
      </div>

      {/* Match Content - FotMob style: name logo | time | logo name */}
      <div className="fotmob-fixture-content">
        {/* Home Team */}
        <div className="fotmob-fixture-team home">
          <span className="fotmob-fixture-team-name">{fixture.home_team.name}</span>
          {fixture.home_team.crest_url && (
            <img src={fixture.home_team.crest_url} alt="" className="fotmob-fixture-team-logo" />
          )}
        </div>

        {/* Score/Time */}
        <div className="fotmob-fixture-center">
          {isFinished ? (
            <span className={`fotmob-fixture-score ${resultClass}`}>
              {fixture.home_score} - {fixture.away_score}
            </span>
          ) : (
            <span className="fotmob-fixture-time">{formatTime(fixture.kickoff_utc)}</span>
          )}
        </div>

        {/* Away Team */}
        <div className="fotmob-fixture-team away">
          {fixture.away_team.crest_url && (
            <img src={fixture.away_team.crest_url} alt="" className="fotmob-fixture-team-logo" />
          )}
          <span className="fotmob-fixture-team-name">{fixture.away_team.name}</span>
        </div>
      </div>
    </div>
  )
}
